'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { Message } from 'ai';
import {
  type ChatTranscript,
  type ChatTurn,
  generateChatId,
  getChatTranscript,
  upsertChatTranscript,
} from '@/lib/chat-history';
import { parseAssistantMessage, unwrapUserInput } from '@/lib/parse-assistant-message';
import { writeSearchReturnStash } from '@/lib/search-return-stash';
import { transcriptAttachmentsFromMessage } from '@/lib/chat-transcript-attachments';
import { getLinkPreviewSnapshot } from '@/lib/link-preview/link-preview-cache';

/** A link's preview, when this tab already has it, for the saved copy. */
const previewForTranscript = (url: string) => getLinkPreviewSnapshot(url).preview;
import type { AlgoliaHit } from 'shared/src/types/search.type';

interface ClassificationSnapshot {
  searchType?: string;
  intent?: string;
}

interface PageContextSnapshot {
  pageContext?: string;
  productTitles?: string[];
  shouldUsePageContext?: boolean;
}

interface UseChatTranscriptPersistenceArgs {
  messages: Message[];
  locale: string;
  /** When true, persistence is enabled (i.e. product-search intent is active). */
  enabled: boolean;
  /** Debounce delay before each upsert (ms). */
  debounceMs?: number;
  pageContext?: PageContextSnapshot;
  classification?: ClassificationSnapshot;
  /** Live numeric budget for this search — persisted so a Back-resume restores the pill. */
  budget?: ChatTranscript['budget'];
  /** Optional callback when a new chatId is allocated for a fresh session. */
  onChatIdAllocated?: (chatId: string) => void;
}

interface UseChatTranscriptPersistenceReturn {
  /** Current transcript id (null until first persist). */
  chatId: string | null;
  /** Force a new chatId on the next persist (call when starting a fresh chat). */
  resetChatId: () => void;
  /** Adopt an existing chatId (used by resume to overwrite that transcript). */
  adoptChatId: (chatId: string) => void;
  /** Mark the current chat as completed with a final searchQuery + redirect href. */
  finalize: (finalSearchQuery?: string, finalHref?: string, finalShelf?: AlgoliaHit[]) => void;
}

/**
 * Listens to useChat `messages` and upserts a persisted ChatTranscript to
 * localStorage on each meaningful update. Reuses an in-memory chatId so all
 * subsequent updates within a single chat overwrite the same record.
 *
 * Resume semantics: when `adoptChatId(existingId)` is called, future upserts
 * overwrite that existing transcript (effectively truncating future turns
 * after the resumed checkpoint).
 */
export function useChatTranscriptPersistence({
  messages,
  locale,
  enabled,
  debounceMs = 300,
  pageContext,
  classification,
  budget,
  onChatIdAllocated,
}: UseChatTranscriptPersistenceArgs): UseChatTranscriptPersistenceReturn {
  const chatIdRef = useRef<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const pageContextSnapshotRef = useRef<PageContextSnapshot | undefined>(pageContext);
  const classificationSnapshotRef = useRef<ClassificationSnapshot | undefined>(classification);

  // Capture page context + classification only at the very first user message of a chat
  // so the original context is preserved even if the user navigates around mid-chat.
  useEffect(() => {
    if (chatIdRef.current === null && messages.length === 0) {
      pageContextSnapshotRef.current = pageContext;
      classificationSnapshotRef.current = classification;
    }
  }, [pageContext, classification, messages.length]);

  const resetChatId = useCallback(() => {
    chatIdRef.current = null;
    startedAtRef.current = null;
  }, []);

  const adoptChatId = useCallback((id: string) => {
    chatIdRef.current = id;
    const existing = getChatTranscript(id);
    startedAtRef.current = existing?.startedAt ?? Date.now();
    if (existing?.pageContextSnapshot) {
      pageContextSnapshotRef.current = existing.pageContextSnapshot;
    }
    if (existing?.classification) {
      classificationSnapshotRef.current = existing.classification;
    }
  }, []);

  const buildTranscript = useCallback((): ChatTranscript | null => {
    if (!enabled) return null;
    if (!messages || messages.length === 0) return null;

    // Find first user message (skip any system warm-ups)
    const firstUser = messages.find((m) => m.role === 'user');
    if (!firstUser) return null;

    // Extract the original raw user query (unwrap our "[User's answer …]" wrappers if present)
    const initialUserInput = unwrapUserInput(firstUser.content);
    // Links and notes the shopper sent are saved too (photos never are).
    const initialAttachments = transcriptAttachmentsFromMessage(firstUser, previewForTranscript);

    // Allocate a chatId on the first persist
    if (chatIdRef.current === null) {
      chatIdRef.current = generateChatId();
      startedAtRef.current = Date.now();
      onChatIdAllocated?.(chatIdRef.current);
    }

    // Build turns from consecutive (user → assistant) pairs.
    const turns: ChatTurn[] = [];
    let turnIndex = 0;
    let pendingUserInput: string | undefined = undefined;
    let pendingUserAttachments: ChatTurn['userAttachments'] = undefined;

    for (const msg of messages) {
      // Ignore internal region-indication messages from persisted transcript
      if (msg.id === 'region-indication') continue;

      if (msg.role === 'user') {
        pendingUserInput = unwrapUserInput(msg.content);
        pendingUserAttachments = transcriptAttachmentsFromMessage(msg, previewForTranscript);
        continue;
      }

      if (msg.role === 'assistant') {
        const parsed = parseAssistantMessage({ message: msg });
        turns.push({
          index: turnIndex,
          timestamp: Date.now(),
          userInput: pendingUserInput,
          ...(pendingUserAttachments ? { userAttachments: pendingUserAttachments } : {}),
          rawAssistantContent: msg.content,
          parsed: {
            message: parsed.message ?? null,
            prompt: parsed.prompt,
            suggestedUserAnswers: parsed.suggestedUserAnswers,
            searchQuery: parsed.searchQuery,
            isDone: parsed.isDone,
            turnMode: parsed.turnMode ?? 'refine',
          },
        });
        turnIndex += 1;
        pendingUserInput = undefined;
        pendingUserAttachments = undefined;
      }
    }

    if (turns.length === 0) return null;

    return {
      id: chatIdRef.current,
      locale,
      startedAt: startedAtRef.current ?? Date.now(),
      updatedAt: Date.now(),
      initialUserInput,
      ...(initialAttachments ? { initialAttachments } : {}),
      pageContextSnapshot: pageContextSnapshotRef.current,
      classification: classificationSnapshotRef.current,
      ...(budget ? { budget } : {}),
      turns,
    };
  }, [enabled, locale, messages, onChatIdAllocated, budget]);

  // Debounced persist on every messages change while enabled
  useEffect(() => {
    if (!enabled) return;
    if (messages.length === 0) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const t = buildTranscript();
      if (t) upsertChatTranscript(t);
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [messages, enabled, debounceMs, buildTranscript]);

  const finalize = useCallback((finalSearchQuery?: string, finalHref?: string, finalShelf?: AlgoliaHit[]) => {
    if (!chatIdRef.current) return;
    const existing = getChatTranscript(chatIdRef.current);
    if (!existing) return;
    upsertChatTranscript({
      ...existing,
      finalSearchQuery: finalSearchQuery ?? existing.finalSearchQuery,
      finalHref: finalHref ?? existing.finalHref,
      finalShelf: finalShelf ?? existing.finalShelf,
      updatedAt: Date.now(),
    });
    // A result link is about to navigate the user away — leave a breadcrumb so a
    // plain Back (a same-document popstate for every soft-nav exit, which gives
    // no reliable "we came back" signal on its own) knows which transcript to
    // restore. See lib/search-return-stash.ts.
    writeSearchReturnStash(chatIdRef.current);
  }, []);

  return {
    chatId: chatIdRef.current,
    resetChatId,
    adoptChatId,
    finalize,
  };
}

